import { Box, ColorOptions, expiry as validateExpiry, MaterialTextInput, Spacer, Text, TextInputNew } from '@jfs/ui-new'
import { useEffect, useState } from 'react'
import { TextInput } from 'react-native'
import { convertHexToRGBA, formatExpiryDate, getExpiryMessage } from '../../utils'
import { CVVInput } from './CVVInput'
import { creditCardStyles } from './styles'

interface ExpiryCVVInputProps {
  value?: string
  innerRef1?: React.RefObject<TextInput>
  innerRef2?: React.RefObject<TextInput>
  prevRef?: React.RefObject<TextInput>
  nextRef?: React.RefObject<TextInput>
  maxCvvLength: number
  isValidExpiry: (value: boolean) => void
  isValidCVV: (value: boolean) => void
  expiry: string
  setExpiry: (value: string) => void
  cvv: string
  setCvv: (value: string) => void
  validCvv: boolean
  theme?: any
  fotterComp?: React.ReactNode
  type?: 'default' | 'side_labels'
}

const ExpiryCVVInput = ({
  innerRef1,
  innerRef2,
  prevRef,
  nextRef,
  maxCvvLength,
  isValidExpiry,
  isValidCVV,
  expiry,
  cvv,
  setExpiry,
  setCvv,
  validCvv,
  theme,
  fotterComp,
  type = 'default',
}: ExpiryCVVInputProps) => {
  const [errorMsgExpiry, setErrorMsgExpiry] = useState('')
  const styles = creditCardStyles(theme)

  const internalOnChangeExpiry = async (text: string) => {
    errorMsgExpiry && setErrorMsgExpiry('')
    const numberWithoutSpace = text.replace(/[^0-9]/g, '')
    const formattedExpiry = formatExpiryDate(numberWithoutSpace)

    if (formattedExpiry.length !== 7) {
      isValidExpiry(false)
    } else {
      validateExpiry
        .validate(formattedExpiry.replace(' / ', '/'))
        .then((res) => {
          if (res === formattedExpiry.replace(' / ', '/')) {
            isValidExpiry(true)
          }
        })
        .catch(() => {
          isValidExpiry(false)
          setErrorMsgExpiry(getExpiryMessage(formattedExpiry))
        })
    }

    setExpiry(formattedExpiry)
  }

  // Check if the expiry length is equal to the 7
  useEffect(() => {
    if (expiry.length === 7 && getExpiryMessage(expiry).length === 0) {
      innerRef2?.current?.focus()
    }
  }, [expiry, expiry.length, innerRef2])

  const internalOnExpiryPress = ({ nativeEvent }: { nativeEvent: any }) => {
    if (nativeEvent?.key === 'Backspace' && !expiry.length) {
      prevRef?.current?.focus()
    }
  }

  const rightComp = () => {
    if (type === 'default') {
      return null
    }
    return (
      <Text
        title={'MM/YY'}
        fontFamily={'poppins-regular'}
        fontSize={10}
        color={convertHexToRGBA(theme?.bodyTextColor, 40) as ColorOptions}
      />
    )
  }

  const onInternalExpiryBlur = () => {
    try {
      if (expiry.length < 7) {
        throw new Error()
      }
    } catch (error) {
      setErrorMsgExpiry('Invalid expiry date')
      isValidExpiry(false)
    }
  }

  return (
    <>
      <Box {...styles.cvvContainer} align={type === 'side_labels' ? 'flex-start' : 'center'}>
        <Box width="50%">
          {type !== 'side_labels' && (
            <MaterialTextInput
              innerRef={innerRef1}
              onKeyPress={internalOnExpiryPress}
              maxLength={7}
              value={expiry}
              onChangeText={(text: string) => internalOnChangeExpiry(text)}
              onBlur={onInternalExpiryBlur}
              placeholderTextColor={convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions}
              placeholder={'Expiry (MM/YY)'}
              color={theme?.bodyTextColor}
              keyboardType={'number-pad'}
              error={errorMsgExpiry}
              testID={'expiryDateTextInput'}
              bottomLineColor={theme?.brandColor as ColorOptions}
              tintColor={theme?.brandColor as ColorOptions}
            />
          )}
          {type === 'side_labels' && (
            <TextInputNew
              innerRef={innerRef1}
              onKeyPress={internalOnExpiryPress}
              maxLength={7}
              value={expiry}
              onChangeText={(text: string) => internalOnChangeExpiry(text)}
              onBlur={onInternalExpiryBlur}
              placeholderTextColor={convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions}
              color={theme?.bodyTextColor}
              keyboardType={'number-pad'}
              error={errorMsgExpiry}
              touched={true}
              testID={'expiryDateTextInput'}
              borderColor={convertHexToRGBA(theme?.brandColor, 40) as ColorOptions}
              focusBorderColor={convertHexToRGBA(theme?.brandColor, 40) as ColorOptions}
              borderRadius={8}
              paddingHorizontal={8}
              paddingVertical={14}
              type={'body10'}
              fontSize={12}
              showBottomLine={false}
              rightComponent={rightComp()}
            />
          )}
        </Box>
        <Spacer direction={'horizontal'} size={'SMALL'} />
        <Box width={type === 'side_labels' ? '46%' : '47%'}>
          <CVVInput
            innerRef={innerRef2}
            nextRef={nextRef}
            prevRef={innerRef1}
            isValidCVV={isValidCVV}
            cvv={cvv}
            setCvv={setCvv}
            maxCvvLength={maxCvvLength}
            validCvv={validCvv}
            theme={theme}
            isTextInput={type === 'side_labels'}
            type={type}
          />
        </Box>
      </Box>
      {fotterComp ? fotterComp : undefined}
    </>
  )
}

export { ExpiryCVVInput }
